import React, { useMemo, useRef, useState } from 'react';
import { Button, Empty, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNotify, useQueryWithStore } from 'react-admin';
import { useHistory, useLocation } from 'react-router-dom';
import MainCard from '../Common/MainCard';
import { NotFoundComponent } from '../Common/utils';
import {
    SETTINGS,
    SETTING_GROUPS,
    STATUS_LABELS,
    SettingDescriptor,
    SettingStatus,
    matches,
    statusOf,
} from './registry';
import './Settings.css';

/** Where a setting stands, as a word and a mark — never as a colour alone. */
const StatusPill = ({ status }: { status: SettingStatus }) => (
    <span className={`gs-pill gs-pill--${status}`}>
        <span className="gs-pill__dot" aria-hidden="true" />
        {STATUS_LABELS[status]}
    </span>
);

const SettingsConsole = () => {
    const location = useLocation();
    const history = useHistory();
    const notify = useNotify();
    const [term, setTerm] = useState('');
    const panel = useRef<HTMLDivElement>(null);

    /**
     * Save from the header, for whichever form is open.
     *
     * Each of these forms owns its own antd Form instance and its own submit
     * handler, so rather than lifting thirteen of them out one by one, the
     * header submits the panel's actual <form> element. antd validates and
     * calls that form's own onFinish exactly as its own button did.
     */
    const save = () => {
        const form = panel.current?.querySelector('form');
        if (form) form.requestSubmit();
        else notify('This setting is still loading', 'info');
    };

    /**
     * Every setting, once. The rail says where each one stands before it is
     * opened, and that answer is the point of the page — so it cannot wait for
     * thirteen forms to each fetch their own.
     *
     * Read from the store, so a form saving through the data provider updates
     * the rail behind it without this having to know a save happened.
     */
    const { data, loaded } = useQueryWithStore({
        type: 'getList',
        resource: 'general_settings',
        payload: {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
            filter: {},
        },
    });

    /** the stored record for each setting, by the name it is stored under */
    const records = useMemo(() => {
        const byName: Record<string, any> = {};
        ((data as any[]) || []).forEach((record) => {
            if (record?.name) byName[record.name] = record;
        });
        return byName;
    }, [data]);

    const found = useMemo(() => SETTINGS.filter((setting) => matches(setting, term)), [term]);

    /**
     * Which one is open, in the URL — so a setting can be linked to, and the
     * page comes back to it on a reload. Falls back to the first thing the
     * search actually turned up rather than to something now hidden.
     */
    const asked = new URLSearchParams(location.search).get('s');
    const current: SettingDescriptor | undefined =
        found.find((setting) => setting.key === asked) || found[0];

    const select = (key: string) => {
        const params = new URLSearchParams(location.search);
        params.set('s', key);
        history.replace(`${location.pathname}?${params.toString()}`);
    };

    const status = current ? statusOf(current, records[current.key]) : null;

    return (
        <div className="netorca-gs netorca-frame">
            <MainCard className="gs-panel">
                <div className="gs-console">
                    {/* ---- the rail: everything there is, and where it stands ---- */}
                    {/* the rail's frame stretches to the card; what is inside it
                        stays with the reader as the panel beside it scrolls */}
                    <nav className="gs-rail" aria-label="Settings">
                      <div className="gs-rail__inner">
                        <div className="gs-search">
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                placeholder="Search settings"
                                value={term}
                                onChange={(event) => setTerm(event.target.value)}
                                aria-label="Search settings"
                                data-cy="settings-search"
                            />
                        </div>

                        <div className="gs-rail__scroll">
                            {found.length === 0 && (
                                <p className="gs-rail__none">Nothing matches “{term}”.</p>
                            )}

                            {SETTING_GROUPS.map((group) => {
                                const inGroup = found.filter((setting) => setting.group === group);
                                if (inGroup.length === 0) return null;

                                return (
                                    <section className="gs-group" key={group}>
                                        <h2 className="gs-group__title">{group}</h2>
                                        <ul className="gs-group__list">
                                            {inGroup.map((setting) => {
                                                const state = statusOf(setting, records[setting.key]);
                                                const active = current?.key === setting.key;

                                                return (
                                                    <li key={setting.key}>
                                                        <button
                                                            type="button"
                                                            className={`gs-item${active ? ' gs-item--active' : ''}`}
                                                            aria-current={active ? 'true' : undefined}
                                                            onClick={() => select(setting.key)}
                                                            data-cy={`settings-nav-${setting.key}`}
                                                        >
                                                            <span className="gs-item__name">{setting.title}</span>
                                                            {loaded && state && <StatusPill status={state} />}
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </section>
                                );
                            })}
                        </div>
                      </div>
                    </nav>

                    {/* ---- the one being edited ---- */}
                    <section className="gs-detail" aria-live="polite">
                        {current ? (
                            <>
                                <header className="gs-detail__head">
                                    <div className="gs-detail__lead">
                                        <h1 className="gs-detail__title">{current.title}</h1>
                                        <p className="gs-detail__desc">{current.description}</p>
                                    </div>

                                    {/* where it stands and what to do about it, at
                                        the top — a settings form has no natural end
                                        to put a button after, and thirteen of them
                                        each ended in a different place */}
                                    <div className="gs-detail__actions">
                                        {loaded && status && <StatusPill status={status} />}
                                        <Button type="primary" onClick={save} data-cy="settings-save">
                                            Save
                                        </Button>
                                    </div>
                                </header>

                                {/* The forms are each their own component and fetch what
                                    they need themselves. Keyed so switching settings
                                    rebuilds the form rather than handing the next one
                                    the last one's field values. */}
                                <div className="gs-detail__form" key={current.key} ref={panel}>
                                    <current.Form />
                                </div>
                            </>
                        ) : (
                            <Empty className="gs-detail__empty" description="No setting selected" />
                        )}
                    </section>
                </div>
            </MainCard>
        </div>
    );
};

/**
 * Everything this deployment is configured to do, on one page.
 *
 * It was thirteen collapsed panels in a flat list, so the question a settings
 * page exists to answer — what is switched on here — could only be answered by
 * opening all thirteen. Now the rail answers it standing still: every setting,
 * grouped by what it governs, each showing where it stands, searchable, and the
 * open one named in the URL so it can be linked to.
 */
const GeneralSettingsList = () => {
    const user = JSON.parse(localStorage.getItem('user') as string);

    if (!user?.is_superuser) return NotFoundComponent;

    return <SettingsConsole />;
};

export default GeneralSettingsList;
